import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleFilled, CloseCircleFilled, EyeInvisibleOutlined, EyeOutlined } from 'antd/icons';
import { Input } from 'antd';
import { ANSI_SYMBOL } from '@lib/passwordRules';
import styled from '@emotion/styled';
import UnstyledButton from '@components/UI/UnstyledButton';
import usePasswordVisibility from '@components/FormInputs/usePasswordVisibility';

const Crossed = ({ crossed, matches }: { crossed: boolean; matches: boolean }) => {
  return (
    <span style={{ display: crossed || matches ? 'inline' : 'none', color: crossed ? 'red' : 'green' }}>
      {crossed && <CloseCircleFilled />}
      {matches && <CheckCircleFilled />}
    </span>
  );
};

type PasswordIconsProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  visible: boolean;
  valid: boolean;
  matches: boolean;
};

const PasswordIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const PasswordIcons = ({ visible, valid, matches, ...props }: PasswordIconsProps) => (
  <UnstyledButton data-testid="password-icons-button" {...props}>
    <PasswordIconContainer>
      <Crossed crossed={!valid} matches={matches} />
      <span>{visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}</span>
    </PasswordIconContainer>
  </UnstyledButton>
);

type ConfirmPasswordProps = {
  onChange: (confirm: string) => void;
  password: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-required'?: React.AriaAttributes['aria-required'];
};

const ConfirmPassword = ({
  onChange,
  password,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired
}: ConfirmPasswordProps) => {
  const { t } = useTranslation('common');
  const [focussedOnPassword, setFocussedOnPassword] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [illegalCharacterPressed, setIllegalCharacterPressed] = useState(false);
  const {
    visible: visiblePassword,
    toggleProps: passwordToggleProps,
    announcement: passwordVisibilityAnnouncement
  } = usePasswordVisibility();

  useEffect(() => {
    if (illegalCharacterPressed) {
      const timer = setInterval(() => setIllegalCharacterPressed(false), 250);

      return () => clearInterval(timer);
    }

    return () => {};
  }, [illegalCharacterPressed]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirm(e.target.value);
    if (focussedOnPassword) {
      onChange(e.target.value);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;

    if (key.length === 1) {
      if (!ANSI_SYMBOL.test(key)) {
        event.preventDefault();
        setIllegalCharacterPressed(true);
      }
    }
  };

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (text) {
      if (!ANSI_SYMBOL.test(text)) {
        event.preventDefault();
        setIllegalCharacterPressed(true);
      }
    }
  };

  return (
    <div>
      <Input
        tabIndex={0}
        id={id}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        type={visiblePassword ? 'text' : 'password'}
        placeholder={t('Password')}
        className="login-input"
        value={confirm}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onChange={handleChange}
        onFocus={() => setFocussedOnPassword(true)}
        onBlur={() => setFocussedOnPassword(false)}
        autoComplete="new-password"
        suffix={
          <>
            <PasswordIcons
              visible={visiblePassword}
              valid={password === confirm}
              matches={!!password && password === confirm}
              {...passwordToggleProps}
            />
            {passwordVisibilityAnnouncement}
          </>
        }
      />
    </div>
  );
};

export default ConfirmPassword;
